<template>
  <div class="contact-page">
    <BlogNav />

    <!-- HERO SECTION -->
    <section class="contact-hero">
      <div class="hero-content">
        <h1 class="hero-title">Contactez-nous</h1>
        <p class="hero-subtitle">
          Prenez contact avec notre équipe, nous reviendrons vers vous rapidement
        </p>
      </div>
    </section>

    <!-- FORM SECTION -->
    <section class="form-section">
      <div class="form-container">
        <form @submit.prevent="validateAndSubmit" class="contact-form">
          <!-- Nom et Prénom -->
          <div class="form-row">
            <div class="form-group">
              <label for="nom">Nom <span class="required">*</span></label>
              <input
                type="text"
                id="nom"
                v-model="form.nom"
                required
                @blur="validateField('nom')"
                :class="{'input-error': errors.nom}"
                placeholder="Votre nom"
              />
              <span v-if="errors.nom" class="error-message">{{ errors.nom }}</span>
            </div>

            <div class="form-group">
              <label for="prenom">Prénom <span class="required">*</span></label>
              <input
                type="text"
                id="prenom"
                v-model="form.prenom"
                required
                @blur="validateField('prenom')"
                :class="{'input-error': errors.prenom}"
                placeholder="Votre prénom"
              />
              <span v-if="errors.prenom" class="error-message">{{ errors.prenom }}</span>
            </div>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label for="email">Email <span class="required">*</span></label>
            <input
              type="email"
              id="email"
              v-model="form.email"
              required
              @blur="validateField('email')"
              :class="{'input-error': errors.email}"
              placeholder="votre@email.com"
            />
            <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
          </div>

          <!-- Téléphone -->
          <div class="form-group">
            <label for="telephone">Téléphone <span class="required">*</span></label>
            <input
              type="tel"
              id="telephone"
              v-model="form.telephone"
              required
              @blur="validateField('telephone')"
              :class="{'input-error': errors.telephone}"
              placeholder="+33 6 12 34 56 78"
            />
            <span v-if="errors.telephone" class="error-message">{{ errors.telephone }}</span>
          </div>

          <!-- Entreprise/Cabinet -->
          <div class="form-group">
            <label for="cabinet">Entreprise/Cabinet <span class="required">*</span></label>
            <input
              type="text"
              id="cabinet"
              v-model="form.cabinet"
              required
              @blur="validateField('cabinet')"
              :class="{'input-error': errors.cabinet}"
              placeholder="Nom de votre entreprise"
            />
            <span v-if="errors.cabinet" class="error-message">{{ errors.cabinet }}</span>
          </div>

          <!-- Message -->
          <div class="form-group">
            <label for="message">Message (optionnel)</label>
            <textarea
              id="message"
              v-model="form.message"
              rows="5"
              placeholder="Parlez-nous de votre projet..."
            ></textarea>
          </div>

          <div class="required-note">
            <span class="required">*</span> Tous les champs sont obligatoires
          </div>

          <button class="submit-button" type="submit" :disabled="isSubmitting">
            <span v-if="isSubmitting">
              <span class="spinner"></span> Envoi en cours...
            </span>
            <span v-else>Envoyer</span>
          </button>

          <div v-if="responseMessage" class="response-message" :class="{ 'success': isSuccess, 'error': !isSuccess }">
            {{ responseMessage }}
          </div>
        </form>

        <!-- INFO SIDEBAR -->
        <div class="info-sidebar">
          <div class="info-card">
            <div class="info-icon">📧</div>
            <h3>Email</h3>
            <p>contact@query-juriste.com</p>
          </div>

          <div class="info-card">
            <div class="info-icon">📅</div>
            <h3>Réserver une démo</h3>
            <p>Planifiez un rendez-vous avec notre équipe</p>
            <a
              href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
              @click.prevent="openCalendlyDemo"
              target="_blank"
              rel="noopener"
              class="demo-link"
            >
              Réserver
            </a>
          </div>

          <div class="info-card">
            <div class="info-icon">⏱️</div>
            <h3>Délai de réponse</h3>
            <p>Nous répondons généralement sous 24h</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-logo-section">
          <img src="/logo-query-juriste-png.png" alt="Query Juriste" class="logo" />
          <p class="footer-description">
            L'IA juridique qui raisonne avec vos besoins.
            Simplifiez vos recherches juridiques.
          </p>
        </div>

        <div class="footer-links-grid">
          <div class="footer-column">
            <h4>Pages</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/">Accueil</a></li>
              <li><a href="https://www.query-juriste.com/blog">Blog</a></li>
              <li><a href="https://www.query-juriste.com/ia">L'IA Query Juriste</a></li>
              <li><a href="https://www.query-juriste.com/faq">FAQ</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Produit</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/loginPage">Connexion</a></li>
              <li><a href="https://www.query-juriste.com/contactPage">Contact</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Entreprise</h4>
            <ul>
              <li><a href="https://www.query-juriste.com/mentionsPage">Mentions légales</a></li>
              <li><a href="https://www.query-juriste.com/conditions-utilisation">CGU</a></li>
              <li><a href="https://www.query-juriste.com/conditions-vente">CGV</a></li>
              <li><a href="https://www.query-juriste.com/politique-donnees">Confidentialité</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>© 2025 Query Juriste, tous droits réservés</p>
      </div>
    </footer>
  </div>
</template>

<script>
import BlogNav from "@/components/BlogNav.vue";
import axios from "axios";
import { openCalendlyDemo } from "@/utils/calendly";

export default {
  name: "FleetContactPage",
  components: { BlogNav },
  data() {
    return {
      form: {
        nom: "",
        prenom: "",
        email: "",
        telephone: "",
        cabinet: "",
        message: "",
      },
      errors: {
        nom: "",
        prenom: "",
        email: "",
        telephone: "",
        cabinet: "",
      },
      responseMessage: "",
      isSubmitting: false,
      isSuccess: false,
    };
  },
  methods: {
    openCalendlyDemo,
    validateField(field) {
      this.errors[field] = "";

      // Check if empty
      if (!this.form[field].trim()) {
        this.errors[field] = `Ce champ est obligatoire`;
        return false;
      }

      // Additional validation for specific fields
      if (field === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.form.email)) {
          this.errors.email = "Veuillez entrer une adresse email valide";
          return false;
        }
      }

      return true;
    },

    validateForm() {
      let isValid = true;

      // Validate all required fields
      const requiredFields = ['nom', 'prenom', 'email', 'telephone', 'cabinet'];
      for (const field of requiredFields) {
        if (!this.validateField(field)) {
          isValid = false;
        }
      }

      return isValid;
    },

    validateAndSubmit() {
      if (this.validateForm()) {
        this.submitForm();
      } else {
        this.responseMessage = "Veuillez remplir tous les champs obligatoires correctement.";
        this.isSuccess = false;
      }
    },

    async submitForm() {
      this.isSubmitting = true;
      this.responseMessage = "";
      
      try {
        const response = await axios.post("https://api.query-juriste.com/copilot/contact", this.form);
        if (response.data.res === "done") {
          this.responseMessage = "Votre message a bien été envoyé! Nous reviendrons vers vous rapidement.";
          this.isSuccess = true;
          this.clearForm();
        } else {
          this.responseMessage = "Une erreur est survenue lors de l'envoi de votre message.";
          this.isSuccess = false;
        }
      } catch (error) {
        this.responseMessage = "Une erreur s'est produite. Veuillez réessayer plus tard.";
        this.isSuccess = false;
        console.error("Error submitting form:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    clearForm() {
      // Reset form data
      for (const field in this.form) {
        this.form[field] = "";
      }

      // Reset error messages
      for (const field in this.errors) {
        this.errors[field] = "";
      }
    },
  },
  metaInfo() {
    return {
      title: "Contact - Query Juriste",
      meta: [
        {
          name: "description",
          content: "Contactez l'équipe Query Juriste pour toute question ou demande de démonstration.",
        },
      ],
    };
  },
};
</script>

<style scoped>
/* ---------- Tokens ---------- */
:root {
  --bg: #ffffff;
  --text: #1a1a1a;
  --muted: #5e6470;
  --primary: #625CFB;
  --primary-600: #5147e0;
  --surface: #f7f8fa;
  --border: #e6e8ec;
  --shadow: 0 10px 30px rgba(0,0,0,.12);
  --shadow-sm: 0 4px 16px rgba(0,0,0,.08);
  --radius: 14px;
}

/* ---------- Layout ---------- */
.contact-page {
  font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif;
  background: var(--bg);
}

/* ---------- HERO ---------- */
.contact-hero {
  background: linear-gradient(135deg, #f8f7ff 0%, #ffffff 100%);
  padding: 140px 20px 80px;
  text-align: center;
}
.hero-content {
  max-width: 800px;
  margin: 0 auto;
}
.hero-title {
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--text);
}
.hero-subtitle {
  font-size: clamp(16px, 2.5vw, 20px);
  color: var(--muted);
  line-height: 1.6;
}

/* ---------- FORM SECTION ---------- */
.form-section {
  padding: 80px 20px;
  background: #fff;
}
.form-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 60px;
}

/* ---------- FORM ---------- */
.contact-form {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 40px;
  box-shadow: var(--shadow-sm);
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.form-group {
  margin-bottom: 24px;
}
label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text);
  font-size: 15px;
}
input,
textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border);
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.2s ease;
  background: var(--surface);
}
input:focus,
textarea:focus {
  outline: none;
  border-color: var(--primary);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(98, 92, 251, 0.1);
}
input::placeholder,
textarea::placeholder {
  color: #999;
}
textarea {
  resize: vertical;
  min-height: 120px;
}
.input-error {
  border-color: #e53935 !important;
  background-color: rgba(229, 57, 53, 0.05) !important;
}
.error-message {
  color: #e53935;
  font-size: 13px;
  margin-top: 6px;
  display: block;
  font-weight: 500;
}
.required {
  color: #e53935;
  font-weight: 700;
}
.required-note {
  font-size: 14px;
  margin-bottom: 24px;
  color: var(--muted);
}
.submit-button {
  width: 100%;
  padding: 16px 32px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.submit-button:hover:not(:disabled) {
  background: var(--primary-600);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(98, 92, 251, 0.3);
}
.submit-button:active:not(:disabled) {
  transform: translateY(0);
}
.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.response-message {
  margin-top: 20px;
  padding: 16px;
  border-radius: 10px;
  text-align: center;
  font-weight: 500;
  font-size: 15px;
}
.success {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
.error {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
}

/* ---------- SPINNER ---------- */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s ease-in-out infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ---------- INFO SIDEBAR ---------- */
.info-sidebar {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.info-card {
  background: linear-gradient(135deg, #f8f7ff 0%, #ffffff 100%);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 28px;
  text-align: center;
  transition: all 0.3s ease;
}
.info-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-sm);
  border-color: var(--primary);
}
.info-icon {
  font-size: 48px;
  margin-bottom: 16px;
}
.info-card h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text);
}
.info-card p {
  color: var(--muted);
  margin: 0;
  line-height: 1.5;
}
.demo-link {
  display: inline-block;
  margin-top: 12px;
  padding: 10px 24px;
  background: var(--primary);
  color: #fff;
  text-decoration: none;
  border-radius: 999px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
}
.demo-link:hover {
  background: var(--primary-600);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(98, 92, 251, 0.3);
}

/* ---------- FOOTER ---------- */
.footer {
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 3rem 0 0 0;
}
.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  display: flex;
  justify-content: space-between;
  gap: 4rem;
  align-items: start;
}
.footer-logo-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.footer-logo-section .logo {
  height: 60px;
  width: 60px;
  margin-bottom: 0.5rem;
}
.footer-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
  max-width: 300px;
}
.footer-links-grid {
  display: flex;
  gap: 3rem;
}
.footer-column h4 {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #1a1a1a;
}
.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.footer-column li {
  margin-bottom: 0.75rem;
}
.footer-column a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s;
}
.footer-column a:hover {
  color: var(--primary);
}
.footer-bottom {
  border-top: 1px solid #e5e7eb;
  margin-top: 2rem;
  padding: 1.5rem 3rem;
  text-align: center;
}
.footer-bottom p {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

/* ---------- Responsive ---------- */
@media (max-width: 900px) {
  .form-container {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .contact-form {
    padding: 30px;
  }
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .footer-content {
    flex-direction: column;
    gap: 2rem;
  }
  .footer-links-grid {
    flex-wrap: wrap;
    gap: 2rem;
  }
}

@media (max-width: 640px) {
  .contact-hero {
    padding-top: 100px;
  }
  .contact-form {
    padding: 24px;
  }
  .footer-content,
  .footer-bottom {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
</style>
